/**
 * util functions shared between Insights App and Dashboard insights card
 * moved from iron/apps/insights/src/util/index.js and iron/apps/insights/src/util/chart/index.js
 */
import capitalize from 'lodash/capitalize';
import differenceBy from 'lodash/differenceBy';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import round from 'lodash/round';
import sum from 'lodash/sum';
import memoize from 'lodash/memoize';
import moment from 'moment';
import sumBy from 'lodash/sumBy';
import negate from 'lodash/negate';
import omit from 'lodash/omit';

import {getOwnAttr, nestedObjectDepth, SORT_ORDERING, sorted} from 'common/util';
import {transformLeaves} from 'common/util/chart';
import {getCurrentDate, offsetForHighcharts} from 'common/util/date';
import {
  ChartType,
  PRIMATIVE_MATH_TYPES,
  ProjectFeatureFlag,
  PropertyType,
  ResourceType,
  XAU_MATH_TYPES,
  XAU_ONLY_MATH_TYPES,
} from 'common/report/constants';
import {localizedDate} from 'common/util/date';
import {MS_BY_UNIT} from 'common/util/date';
import {MATH_TYPE, PROPERTIES} from 'common/report/constants';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {INSIGHTS_RESOURCE_TYPES} from 'common/widgets/property-filter-menu/models/insights-filter';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
  TODAY,
} from 'common/widgets/date-range-picker/models/presets';
import {ProjectTimezone} from 'common/report/stores/project-details';
import {renameProperty, renamePropertyValue} from 'common/report/util/properties';
import {getHeaderType, HeaderType, typedComparator} from 'common/util/header-sorting';
import {sortIsoDates} from 'common/util/date';
import {ChartResultChartTypes} from 'common/report/util/insights/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {reporter} from 'common/report/error-reporting';
import {featuresStore} from 'common/report/stores/features';

import {
  AbsoluteDateRange,
  AggregatedSeries,
  DisplayOptions,
  FlatSegment,
  FlatSortingOptions,
  InsightsBookmarkParams,
  InsightsBookmarkSections,
  InsightsQueryResults,
  InsightsResourceType,
  InsightsSeriesTree,
  OrderedSumTree,
  SeriesSortingOptions,
  SortBy,
  SortConfig,
  SortOrder,
  UnorderedSumTree,
  UnorderedSumTreeNode,
} from './types';

export const NUMERIC_DATA_PRECISION = 4;
export const DEFAULT_VISIBLE_SEGMENTS = 12;

function getTimestampsHelper(obj: Obj, objectDepth: number): number[] {
  if (objectDepth === 1) {
    return Object.keys(obj)
      .map((ts) => Number(ts))
      .sort();
  }
  return getTimestampsHelper(obj[Object.keys(obj)[0]], objectDepth - 1);
}

export function getTimestamps(series: Obj): number[] {
  if (isEmpty(series)) {
    return [];
  }
  return getTimestampsHelper(series, nestedObjectDepth(series));
}

export function mergeTimestamps({
  series,
  timeComparisonSeries,
  offsetMs,
  isTimeSeriesData,
}): {
  timestamps: number[];
  timeComparisonTimestamps: number[];
} {
  if (!isTimeSeriesData || (isEmpty(series) && isEmpty(timeComparisonSeries))) {
    return {timestamps: [], timeComparisonTimestamps: []};
  }

  // If at least one of the series is not empty, we expects to generate at least one timestamp list

  let timestamps = getTimestamps(series);
  let timeComparisonTimestamps = getTimestamps(timeComparisonSeries);
  if (offsetMs) {
    if (isEmpty(timestamps)) {
      timestamps = timeComparisonTimestamps.map((timestamp) => Number(timestamp) + offsetMs);
    }
    if (isEmpty(timeComparisonTimestamps)) {
      timeComparisonTimestamps = timestamps.map((timestamp) => Number(timestamp) - offsetMs);
    }
  }
  return {timestamps, timeComparisonTimestamps};
}

function isKeyExistInObject({obj, key}) {
  return isObject(obj) && Object.prototype.hasOwnProperty.call(obj, key);
}

function extractSeriesLeafValue({series, key, nullValue}) {
  let value = isKeyExistInObject({obj: series, key}) ? series[key] : nullValue;

  if (isNil(value)) {
    // This is to handle a backend bug where it sometimes return `null` values.
    value = nullValue;
  }

  return value;
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
function generateSeriesFromInsightsAPIResponseSeriesHelper({
  series,
  timestamps,
  timeComparisonSeries,
  timeComparisonTimestamps,
  nullValue,
  isTimeSeriesData = false,
  objectDepth,
}) {
  const seriesKeys = isObject(series) ? Object.keys(series) : [];
  const timeComparisonSeriesKeys = isObject(timeComparisonSeries) ? Object.keys(timeComparisonSeries) : [];
  const mergedKeys = [...new Set([...seriesKeys, ...timeComparisonSeriesKeys])];
  const newSeries = {};

  mergedKeys.forEach((key) => {
    if (objectDepth === 1) {
      let value = extractSeriesLeafValue({series, key, nullValue});
      let timeComparisonValue = extractSeriesLeafValue({series: timeComparisonSeries, key, nullValue});
      if (isTimeSeriesData) {
        if (!isObject(value)) {
          value = Object.fromEntries(timestamps.map((key) => [key, nullValue]));
        }
        if (isObject(timeComparisonSeries) && !isObject(timeComparisonValue)) {
          timeComparisonValue = Object.fromEntries(timeComparisonTimestamps.map((key) => [key, nullValue]));
        }
      }
      newSeries[key] = {value, ...(timeComparisonSeries === null ? {} : {timeComparisonValue})};
    } else {
      const innerSeries = isKeyExistInObject({obj: series, key}) ? series[key] : {};
      let innerTimeComparisonSeries = null;
      if (timeComparisonSeries) {
        innerTimeComparisonSeries = isKeyExistInObject({obj: timeComparisonSeries, key})
          ? timeComparisonSeries[key]
          : {};
      }
      newSeries[key] = generateSeriesFromInsightsAPIResponseSeriesHelper({
        isTimeSeriesData,
        nullValue,
        series: innerSeries,
        timestamps,
        timeComparisonSeries: innerTimeComparisonSeries,
        timeComparisonTimestamps,
        objectDepth: objectDepth - 1,
      });
    }
  });
  return newSeries;
}

export function generateSeriesFromInsightsAPIResponseSeries({
  series,
  timeComparisonSeries = null,
  nullValue = 0,
  offsetMs = null,
  isTimeSeriesData = false,
}) {
  const objectDepth = nestedObjectDepth(series) + (isTimeSeriesData ? -1 : 0);
  const {timestamps, timeComparisonTimestamps} = mergeTimestamps({
    series,
    timeComparisonSeries,
    offsetMs,
    isTimeSeriesData,
  });
  return generateSeriesFromInsightsAPIResponseSeriesHelper({
    isTimeSeriesData,
    nullValue,
    objectDepth,
    series,
    timestamps,
    timeComparisonSeries,
    timeComparisonTimestamps,
  });
}

/**
 * Do a rolling sum of the leaf values of a nested object, in the order of their keys (which are
 * dates).
 * @deprecated
 * @example
 * nestedObjectCumulative({
 *   US: {'2016-06-01': 2, '2016-06-02': 7, '2016-06-03': 9},
 *   UK: {'2016-05-02': 5, '2016-05-02': 8, '2016-05-03': 6},
 * });
 * // {
 * //   US: {'2016-06-01': 2, '2016-06-02': 9, '2016-06-03': 18},
 * //   UK: {'2016-05-02': 5, '2016-05-02': 13, '2016-05-03': 19},
 * // }
 */
export function nestedObjectCumulative(obj) {
  if (Object.values(obj).every((value) => typeof value === `number`)) {
    return getCumulativeTimeseries(obj);
  } else if (isObject(obj) && !Array.isArray(obj)) {
    return mapValues(obj, (value) => nestedObjectCumulative(value));
  } else {
    return obj;
  }
}

export function transformInsightsSeries(
  series: any,
  {
    isTimeSeriesData,
    projectTimezone,
    utcOffset,
  }: {isTimeSeriesData: boolean; projectTimezone: string; utcOffset: number},
) {
  const isoDateTimeToTimestamp = new Map<string, number>();
  const transform = (key, val) => {
    // round numeric values according to the constant
    if (typeof val === `number` && isFinite(val)) {
      val = round(val, NUMERIC_DATA_PRECISION);
    }
    if (isTimeSeriesData) {
      const cachedTimestamp = isoDateTimeToTimestamp[key];
      if (cachedTimestamp) {
        return [cachedTimestamp, val];
      }
      // convert ISO-formatted time strings keys into unix timestamps
      const momentKey = moment(key, moment.ISO_8601, true);
      const timestamp = offsetForHighcharts(momentKey, projectTimezone, utcOffset).valueOf();
      isoDateTimeToTimestamp[key] = timestamp;
      return [timestamp, val];
    } else {
      return [key, val];
    }
  };
  return transformLeaves(series, transform);
}

export function getTimeComparisonOffsetMs({dateRange, timeComparisonDateRange}) {
  const fromDateMoment = moment.utc(dateRange.fromDate);
  const timeComparisonFromDateMoment = moment.utc(timeComparisonDateRange.fromDate);
  return fromDateMoment.diff(timeComparisonFromDateMoment, `milliseconds`);
}

/**
 * Computes the relative change from the previous value to the current value.
 * @param {Number} currVal - current value
 * @param {Number} prevVal - previous value
 * @returns {Number} relative change from prevVal to currVal
 */
export function computeDelta(currVal, prevVal) {
  if (!isFinite(currVal) || !isFinite(prevVal) || prevVal === 0) {
    return null;
  }
  return (currVal - prevVal) / prevVal;
}

// region Helper functions for formatDateRanges

function getDateFormatConfig({fromDate, toDate, currentDate}) {
  const fromMoment = moment.parseZone(fromDate);
  const toMoment = moment.parseZone(toDate);
  const startOfDayFromMoment = fromMoment.clone().startOf(`day`);
  const startOfDayToMoment = toMoment.clone().startOf(`day`);
  const endOfDayToMoment = toMoment.clone().endOf(`day`);
  let isMultiDay = true;
  let isPartialDay = true;
  if (fromMoment.isSame(startOfDayFromMoment) && toMoment.isSame(endOfDayToMoment)) {
    if (fromMoment.isSame(startOfDayToMoment)) {
      isMultiDay = false;
    }
    isPartialDay = false;
  }

  let includeYear = true;
  // NOTE: This could be slightly inaccurate because we're not considering using utcOffset
  const currentYear = moment(currentDate).year();
  if (fromMoment.year() === currentYear && toMoment.year() === currentYear) {
    includeYear = false;
  }

  return {isMultiDay, isPartialDay, includeYear};
}

function formatSingleDate(date, {isPartialDay, includeYear}) {
  const DATE_FORMAT = `MMM D`;
  const TIME_FORMAT = `h:mma`;
  const dateMoment = moment.parseZone(date);
  let dateStr = `${dateMoment.format(DATE_FORMAT)}`;
  if (includeYear) {
    dateStr = `${dateStr} ${dateMoment.year()}`;
  }
  if (isPartialDay) {
    dateStr = `${dateStr}, ${dateMoment.format(TIME_FORMAT)}`;
  }
  return dateStr;
}

function formatDateRange({fromDate, toDate}, {isPartialDay = false, includeYear = false, isMultiDay = false} = {}) {
  const formattedFromDate = formatSingleDate(fromDate, {isPartialDay, includeYear});
  if (isMultiDay) {
    const formattedToDate = formatSingleDate(toDate, {isPartialDay, includeYear});
    return `${formattedFromDate} - ${formattedToDate}`;
  } else {
    return formattedFromDate;
  }
}

export function formatDateRanges(
  dateRanges: AbsoluteDateRange[],
  {currentDate = getCurrentDate()}: {currentDate?: Date | moment.Moment} = {},
): string[] {
  const dateFormatConfigs = dateRanges.map(({fromDate, toDate}) => {
    return getDateFormatConfig({fromDate, toDate, currentDate});
  });

  const mergedDateFormatConfig = {};
  Object.keys(dateFormatConfigs[0]).map((configKey) => {
    mergedDateFormatConfig[configKey] = dateFormatConfigs.some((config) => config[configKey]);
  });

  return dateRanges.map((dateRange) => formatDateRange(dateRange, mergedDateFormatConfig));
}

// endregion

/**
 * Returns true if the first time-period has not been completed
 */
export function isIncompleteFirstInterval(
  firstTimestamp: number,
  beginningDate: string,
  {mathOperator, utcOffset = 0}: {mathOperator: MATH_TYPE; utcOffset: number},
): boolean {
  if ((Object.values(XAU_MATH_TYPES) as MATH_TYPE[]).includes(mathOperator)) {
    return false;
  }

  const localizedFirstTimestampDate = new Date(firstTimestamp);
  const localizedBeginningDate = localizedDate({utcOffset, date: new Date(beginningDate)});

  const threshold = MS_BY_UNIT[`hour`]; // add a threshold of 1 hour to account for daylight saving
  return Math.abs(localizedBeginningDate.getTime() - localizedFirstTimestampDate.getTime()) > threshold;
}

/**
 * Returns true if the last time-period has not been completed
 */
export function isIncompleteLastInterval(
  lastTimestamp: number,
  endingDate: string,
  {mathOperator, unit, utcOffset}: {mathOperator: MATH_TYPE; unit: string; utcOffset: number},
): boolean {
  if (!lastTimestamp || !unit) {
    return false;
  }
  if ((Object.values(XAU_MATH_TYPES) as MATH_TYPE[]).includes(mathOperator) && mathOperator !== XAU_MATH_TYPES.DAU) {
    // XAU queries always display as a complete last interval, except DAU
    return false;
  }

  // Calculate interval from the last timestamp to the end of the time range
  const localizedEndingDate = localizedDate({utcOffset, date: new Date(endingDate)});
  const localizedLastTimestampDate = new Date(lastTimestamp);
  const currentInterval = Math.abs(localizedEndingDate.getTime() - localizedLastTimestampDate.getTime());

  // Calculate interval length
  let timeInterval = null,
    start = null,
    end = null;
  switch (unit.toLowerCase()) {
    case `minute`:
    case `hour`:
    case `day`:
    case `week`:
    case `month`:
      timeInterval = MS_BY_UNIT[unit.toLowerCase()];
      break;
    case `quarter`:
      end = new Date(localizedEndingDate.getFullYear(), localizedEndingDate.getMonth() + 1);
      start = new Date(localizedEndingDate.getFullYear(), localizedEndingDate.getMonth() - 2);
      timeInterval = end.getTime() - start.getTime();
      break;
    default:
      console.error(`Unknown interval: "${timeInterval}"`);
      return false;
  }
  const threshold = MS_BY_UNIT[`hour`] + 10 * MS_BY_UNIT[`minute`]; // 1 hour to account for daylight saving, 10 mins because day end at 23:50
  return timeInterval - currentInterval > threshold;
}

const NUMBER_OF_DAYS_IN_UNIT = {
  [TimeUnit.Minute]: 1 / 1440,
  [TimeUnit.Hour]: 1 / 24,
  [TimeUnit.Day]: 1,
  [TimeUnit.Week]: 7,
  [TimeUnit.Month]: 30,
  [TimeUnit.Quarter]: 90,
};

export function getNumberOfDaysInTimeClause(timeClause: any): number {
  const [fromDate, toDate] = Array.isArray(timeClause.value) ? timeClause.value : [null, null];
  switch (timeClause.dateRangeType) {
    case DateRangeType.RelativeAfter:
      return Math.ceil(timeClause.window.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.window.unit]);
    case DateRangeType.Since:
      return timeClause.value === START_OF_TODAY ? 1 : moment().diff(moment(fromDate), `days`);
    case DateRangeType.On:
      return 1;
    case DateRangeType.Between:
      // We could use moment#startOf for start of days but the ranges
      // need to be adjusted for project timezone
      if (fromDate === START_OF_TODAY && toDate === END_OF_TODAY) {
        return 1;
      }
      if (fromDate === START_OF_YESTERDAY && toDate === END_OF_YESTERDAY) {
        return 1;
      }
      return moment(toDate).diff(moment(fromDate), `days`);
    default:
      if (timeClause.value === TODAY) {
        return 1;
      } else if (isNumber(timeClause.value)) {
        return Math.ceil(timeClause.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.unit]);
      } else {
        return moment(toDate).diff(moment(fromDate), `days`);
      }
  }
}

function getSupportedTimeUnitsForXauMathType(mathType: XAU_MATH_TYPES): TimeUnit[] {
  switch (mathType) {
    case XAU_MATH_TYPES.DAU:
      return [TimeUnit.Day];
    case XAU_MATH_TYPES.WAU:
      return [TimeUnit.Day, TimeUnit.Week];
    case XAU_MATH_TYPES.MAU:
      return [TimeUnit.Day, TimeUnit.Week, TimeUnit.Month];

    default: {
      const unreachable: never = mathType; // a trick to enforce full enum coverage in switch case
      throw new Error(`unknown XAU math type "${unreachable}"`);
    }
  }
}

// this is an internal interface that should never be exported outside common utils
interface TimeUnitRestriction {
  supportedUnits: Set<TimeUnit>;

  // the reason given for why time units that are not supported by "supportedUnits" are restricted
  getRestrictionReason: (unit: TimeUnit) => string;
}

// this is an internal method that should never be exported outside common utils
function getSupportedTimeUnitRestrictions(bookmarkParams: any): TimeUnitRestriction[] {
  const showClauses = bookmarkParams.sections.show;
  const [timeClause] = bookmarkParams.sections.time;
  const availableUnits = Object.values(TimeUnit);
  const restrictions: TimeUnitRestriction[] = [];

  const hasCohortsOverTime = showClauses.some(
    (clause) => clause.resourceType === INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_COHORTS,
  );
  if (hasCohortsOverTime) {
    const supportedUnits = new Set<TimeUnit>(availableUnits);
    supportedUnits.delete(TimeUnit.Minute);
    supportedUnits.delete(TimeUnit.Hour);

    restrictions.push({
      supportedUnits,
      getRestrictionReason: (unit) => `${capitalize(unit)} queries not available for cohorts timeseries`,
    });
  }

  // Note that the backend allows for 2 project days
  // but calculates days as calendar days and not the logical
  // 48 hour period, so we limit this to 1 day, another reason
  // for this is to reduce the total number of data points to
  // keep the load light on the browser
  const spansMoreThan24Hours = getNumberOfDaysInTimeClause(timeClause) > 1;
  if (spansMoreThan24Hours) {
    const supportedUnits = new Set<TimeUnit>(availableUnits);
    supportedUnits.delete(TimeUnit.Minute);

    restrictions.push({
      supportedUnits,
      getRestrictionReason: (unit) => `${capitalize(unit)} queries can be performed on 24 hour windows`,
    });
  }

  for (const clause of showClauses) {
    if (XAU_ONLY_MATH_TYPES.includes(clause.math)) {
      const xauLabel = clause.math.toUpperCase();
      restrictions.push({
        supportedUnits: new Set<TimeUnit>(getSupportedTimeUnitsForXauMathType(clause.math)),
        getRestrictionReason: (unit) => `${capitalize(unit)} is unavailable when ${xauLabel} is selected`,
      });
    }
  }

  return restrictions;
}

export function getSupportedTimeUnits(bookmarkParams: any): TimeUnit[] {
  const restrictions = getSupportedTimeUnitRestrictions(bookmarkParams);
  return Object.values(TimeUnit).filter((availableUnit) => {
    return restrictions.every((restriction) => restriction.supportedUnits.has(availableUnit));
  });
}

export function getRestrictionReasonForTimeUnit(bookmarkParams: any, unit: TimeUnit): null | string {
  const restrictions = getSupportedTimeUnitRestrictions(bookmarkParams);
  for (const restriction of restrictions) {
    if (!restriction.supportedUnits.has(unit)) {
      return restriction.getRestrictionReason(unit);
    }
  }

  return null;
}

// TODO: write some tests for this
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getSortConfig(resultHeaders: null | string[], bookmarkSortConfig?: Partial<SortConfig>): SortConfig {
  const sortConfig: SortConfig = {
    bar: {
      sortBy: SortBy.Column,
      colSortAttrs: [
        {
          sortBy: SortBy.Value,
          sortOrder: SortOrder.Descending,
        },
      ],
    },
    line: {
      sortBy: SortBy.Value,
      sortOrder: SortOrder.Descending,
    },
    table: {
      sortBy: SortBy.Column,
      colSortAttrs: [
        {
          sortBy: SortBy.Label,
          sortOrder: SortOrder.Ascending,
        },
      ],
    },
  };

  if (resultHeaders) {
    if (bookmarkSortConfig) {
      // update existing sorting config for new results

      if (bookmarkSortConfig.bar) {
        if (bookmarkSortConfig.bar.sortBy === SortBy.Column) {
          const colSortAttrs = bookmarkSortConfig.bar.colSortAttrs.slice(0, resultHeaders.length);
          for (let i = colSortAttrs.length; i < resultHeaders.length; i++) {
            const header = resultHeaders[i];
            const isDistribution = header === PROPERTIES[`$histogram`];

            // default `Distribution` to sort on A-Z instead of a value sort
            colSortAttrs.push({
              sortBy: isDistribution ? SortBy.Label : SortBy.Value,
              sortOrder: isDistribution ? SortOrder.Ascending : SortOrder.Descending,
            });
          }
          sortConfig.bar.colSortAttrs = colSortAttrs;
        } else {
          sortConfig.bar = bookmarkSortConfig.bar;
        }
      }

      if (bookmarkSortConfig.line) {
        sortConfig.line = bookmarkSortConfig.line;
      }

      if (bookmarkSortConfig.table) {
        if (bookmarkSortConfig.table.sortBy === SortBy.Column) {
          const colSortAttrs = bookmarkSortConfig.table.colSortAttrs.slice(0, resultHeaders.length);
          for (let i = colSortAttrs.length; i < resultHeaders.length; i++) {
            colSortAttrs.push({sortBy: SortBy.Label, sortOrder: SortOrder.Ascending});
          }
          sortConfig.table.colSortAttrs = colSortAttrs;
        } else {
          sortConfig.table = bookmarkSortConfig.table;
        }
      }
    } else {
      // no existing config; ensure that new config has right number of headers

      for (let i = sortConfig.bar.colSortAttrs.length; i < resultHeaders.length; i++) {
        sortConfig.bar.colSortAttrs.push({sortBy: SortBy.Value, sortOrder: SortOrder.Descending});
      }
      for (let i = sortConfig.table.colSortAttrs.length; i < resultHeaders.length; i++) {
        sortConfig.table.colSortAttrs.push({sortBy: SortBy.Label, sortOrder: SortOrder.Ascending});
      }
    }
  }

  return sortConfig;
}

function isPeopleTimeSeries(bookmarkSections: InsightsBookmarkSections) {
  const isPeopleOnly = bookmarkSections.show.every((clause) => clause.resourceType === InsightsResourceType.People);
  const lastGroupClause = bookmarkSections.group && bookmarkSections.group[bookmarkSections.group.length - 1];
  const groupByDatetime = lastGroupClause?.propertyType === PropertyType.Datetime;

  return isPeopleOnly && groupByDatetime;
}

export function getDisplayOptions(
  bookmarkSections: InsightsBookmarkSections,
  bookmarkDisplayOptions: DisplayOptions,
): DisplayOptions {
  const lastGroupClause = bookmarkSections.group && bookmarkSections.group[bookmarkSections.group.length - 1];
  const timeUnit = isPeopleTimeSeries(bookmarkSections) ? lastGroupClause.unit : bookmarkSections.time[0].unit;
  return {
    ...bookmarkDisplayOptions,
    timeUnit,
  };
}

export function isChartTypeDisabled(bookmarkParams: InsightsBookmarkParams, type: ChartType): boolean {
  if (!ChartResultChartTypes.includes(type)) {
    return true;
  }

  const disabledChartTypes = new Set();
  const hasClauses = negate(isEmpty);

  if (hasClauses(bookmarkParams.sections.cohorts) && !hasClauses(bookmarkParams.sections.group)) {
    disabledChartTypes.add(ChartType.StackedBar);
  }

  if (hasClauses(bookmarkParams.sections.formula)) {
    disabledChartTypes.add(ChartType.StackedLine);
    disabledChartTypes.add(ChartType.StackedBar);
    disabledChartTypes.add(ChartType.Pie);
  }

  const hasXauClauses = bookmarkParams.sections.show.some((clause) =>
    (XAU_ONLY_MATH_TYPES as MATH_TYPE[]).includes(clause.math),
  );
  const isCohortsOverTime = bookmarkParams.sections.show.some(
    (clause) => clause.resourceType === InsightsResourceType.Cohorts,
  );
  if (hasXauClauses || isCohortsOverTime) {
    // Exclude all chart types except line
    ChartResultChartTypes.filter((type) => ![ChartType.Line, ChartType.StackedLine].includes(type)).forEach((type) =>
      disabledChartTypes.add(type),
    );
  }

  const isPeopleOnlyQuery =
    hasClauses(bookmarkParams.sections.show) &&
    bookmarkParams.sections.show.every((clause) => clause.resourceType === InsightsResourceType.People);
  if (isPeopleOnlyQuery && !isPeopleTimeSeries(bookmarkParams.sections)) {
    disabledChartTypes.add(ChartType.Line);
    disabledChartTypes.add(ChartType.StackedLine);
  }

  return disabledChartTypes.has(type);
}

/**
 * Flatten a series object tree to show all possible paths + leaf objects of the tree
 * @param {InsightsSeriesTree} series - nested object to be flattened
 * @returns {Array<[string[], Record<string, number>]>} - array of tuples where val1 is a path array and val2 is the corresponding leaf object
 * @example
 * flattenSeries({
 *    "Viewed Report": {
 *      "United States": {
 *        "2020-10-01T00:00:00-07:00": 15,
 *        "2020-11-01T00:00:00-07:00": 1,
 *      },
 *      "Australia": {
 *        "2020-10-01T00:00:00-07:00": 3,
 *        "2020-11-01T00:00:00-07:00": 4,
 *      },
 *    },
 * });
 * // [
 * //   [ ["Viewed Report", "United States"], {"2020-10-01T00:00:00-07:00": 15, "2020-11-01T00:00:00-07:00": 1} ],
 * //   [ ["Viewed Report", "Australia"], {"2020-10-01T00:00:00-07:00": 3, "2020-11-01T00:00:00-07:00": 4} ],
 * // ]
 */
export function flattenSeries(series: InsightsSeriesTree): Array<[string[], Record<string, number>]> {
  const flattenedEntries: Array<[string[], Record<string, number>]> = [];

  // basic breadth-first tree traversal (non-recursive)
  const breadthQueue: Array<[string[], InsightsSeriesTree]> = [[[], series]];
  while (breadthQueue.length > 0) {
    const [path, obj] = breadthQueue.shift();
    for (const [key, value] of Object.entries(obj)) {
      if (isObject(value)) {
        breadthQueue.push([[...path, key], value as InsightsSeriesTree]);
      } else {
        flattenedEntries.push([path, obj as Record<string, number>]);
        break;
      }
    }
  }

  return flattenedEntries;
}

/**
 * Checks if the results series has a single root node with at least one breakdown.
 * Useful to check because for single root breakdowns, the root node is typically ignored when displaying segment names.
 * @param {InsightsQueryResults} results - raw results from an Insights query
 * @returns {boolean}
 */
export function hasSingleRootSeriesWithBreakdown(resultHeaders: string[], resultSeries: InsightsSeriesTree): boolean {
  return resultHeaders.length > 1 && Object.keys(resultSeries).length === 1;
}

/**
 * Converts the Insights query results series into an flat array of data segments
 * @param {InsightsQueryResults} results - raw results from an Insights query
 * @returns {FlatSegment[]}
 */
export function getFlatSegments(resultHeaders: string[], resultSeries: InsightsSeriesTree): FlatSegment[] {
  const unwrapRoot = hasSingleRootSeriesWithBreakdown(resultHeaders, resultSeries);
  const seriesRootKeys = Object.keys(resultSeries);
  const series: InsightsSeriesTree = unwrapRoot
    ? (resultSeries[seriesRootKeys[0]] as InsightsSeriesTree)
    : resultSeries;
  // if the series unwraps its root, remove the first result header to align with the unwrapped series
  const headers = unwrapRoot ? resultHeaders.slice(1) : resultHeaders;

  const flattenedTuples = flattenSeries(series);
  return flattenedTuples.map(([path, value]: [string[], Record<string, number>]) => {
    const displayName = path.map((segment, idx) => renamePropertyValue(segment, headers[idx])).join(` / `);
    const key = path.join();

    // restore full path for use in chart tooltips
    if (unwrapRoot) {
      path.unshift(seriesRootKeys[0]);
    }
    return {key, path, value, displayName, rootUnwrapped: unwrapRoot};
  });
}

/**
 * Do a rolling sum of the values of a timeseries object in the order of their keys (which should be date strings).
 * @param {Record<string, number>} timeseries - timeseries object where keys are ISO-8601 date strings
 * @returns {Record<string, number>} - new timeseries object where values are cumulative
 * @example
 * getCumulativeTimeseries({'2016-05-02': 5, '2016-05-02': 8, '2016-05-03': 6});
 * // {'2016-05-02': 5, '2016-05-02': 13, '2016-05-03': 19}
 */
export function getCumulativeTimeseries(timeseries: Record<string, number>): Record<string, number> {
  // sorting keys just works because ISO-8601 date strings are naturally sortable
  const orderedKeys = Object.keys(timeseries).sort();
  const cumulativeValue: Record<string, number> = {};
  for (let i = 0; i < orderedKeys.length; i++) {
    const previousValue = cumulativeValue[orderedKeys[i - 1]] ?? 0;
    const currentValue = timeseries[orderedKeys[i]];
    cumulativeValue[orderedKeys[i]] = currentValue + previousValue;
  }
  return cumulativeValue;
}

/**
 * Given an unsorted array of flat segments, return a sorted array of flat segments according to an Insights bookmark sort config
 * @param {FlatSegment[]} unsortedSegments - unsorted array of flat segments that will be sorted
 * @param {FlatSortingOptions} sortOptions - flat sorting options found in Insights bookmarks
 * @returns {FlatSegment[]} - array of tuples where val1 is a path array and val2 is a JSON with date string keys and values
 */
export function getSortedFlatSegments(unsortedSegments: FlatSegment[], sortOptions: FlatSortingOptions): FlatSegment[] {
  switch (sortOptions.sortBy) {
    case SortBy.Label: {
      return unsortedSegments.slice().sort(
        typedComparator({
          sortType: HeaderType.STRING,
          order: sortOptions.sortOrder,
          transform: (segment: FlatSegment) => segment.displayName,
        }),
      );
    }
    case SortBy.Value: {
      const order: keyof typeof SORT_ORDERING = sortOptions.sortOrder;

      // memoize transform callback because it is called redundantly during the sort process
      const memoizedTransform = memoize(
        (segment: FlatSegment) => {
          const absoluteMetricValues = Object.values(segment.value).map((metricValue) => Math.abs(metricValue));
          return sum(absoluteMetricValues);
        },
        (segment: FlatSegment) => segment.key,
      );
      return sorted(unsortedSegments, {
        order,
        transform: memoizedTransform,
      });
    }
  }
}

/**
 * Return a timeseries object (where the key is an ISO date string and the value is the metric value for that date)
 * that uses the dates from @mainTimeseries with the values from @comparisonTimeseries.
 * An empty @comparisonTimeseries object will produce a zero-value timeseries.
 * @param {Record<string, number>} mainTimeseries - the timeseries with current dates for the segment
 * @param {Record<string, number>} comparisonTimeseries - a different timeseries, must be either empty or the same key-length as @mainTimeseries
 */
export function getTimeseriesFromTimeComparisonValues(
  mainTimeseries: Record<string, number>,
  comparisonTimeseries: Record<string, number>,
): Record<string, number> {
  const dates = sortIsoDates(Object.keys(mainTimeseries));
  const comparisonDates = Object.keys(comparisonTimeseries);
  const comparisonNotEmpty = comparisonDates.length > 0;
  if (comparisonNotEmpty && dates.length !== comparisonDates.length) {
    throw new Error(`@mainTimeseries and @comparisonTimeseries contain a differing number of date keys`);
  }

  const values = sortIsoDates(comparisonDates).map((key) => comparisonTimeseries[key]);
  return dates.reduce<Record<string, number>>((timeseries, date, idx): Record<string, number> => {
    timeseries[date] = values[idx] ?? 0;
    return timeseries;
  }, {});
}

// converting a date string to a numeric timestamp for Highcharts is a very frequently executed task but very slow with moment.js,
// memoizing the process produces huge performance gains for large datasets
export const memoizedGetTimestamp = memoize(
  (isoDate: string, timezone: string, utcOffset: number) => {
    const date = moment(isoDate, moment.ISO_8601, true);
    return offsetForHighcharts(date, timezone, utcOffset).valueOf();
  },
  (isoDate: string, timezone: string, utcOffset: number) => {
    return [isoDate, timezone, utcOffset].join(`|`);
  },
);

/**
 * Return an array of timestamp tuples from a timeseries object
 * @param {Record<string, number>} timeseries - JSON object where the key is an ISO date string and the value is the metric value for that date
 * @param {ProjectTimezone} ProjectTimezone - details about the project timeseries
 * @returns {Array<[number, number]>} - array of tuples where val1 is a Unix timestamp in milliseconds and val2 is the metric value for that timestamp
 */
export function getTimestampTuplesFromTimeseries(
  timeseries: Record<string, number>,
  projectTimezone: ProjectTimezone,
): Array<[number, number]> {
  return Object.keys(timeseries)
    .sort()
    .map((dateKey: string) => {
      // convert ISO-formatted date string into a Unix timestamp in milliseconds
      const timestamp = memoizedGetTimestamp(dateKey, projectTimezone.timezone, projectTimezone.utcOffset);
      const value = timeseries[dateKey];

      return [timestamp, value];
    });
}

function getFirstTimeseries(series: InsightsSeriesTree): Record<string, number> {
  let node: any = series;
  while (isObject(find(node))) {
    node = find(node);
  }
  return node as Record<string, number>;
}

/**
 * Return dates for each bucket Insights results timeseries. Dates are inferred if possible.
 * @param results - Insights results from query-api
 * @returns - arrays of ISO 8601 date strings for main & time comparison series
 */
export function getTimeseriesDates(results: InsightsQueryResults): {main: string[]; timeComparison: string[]} {
  const timeseries = getFirstTimeseries(results.series);
  let dates = Object.keys(timeseries);

  const comparisonTimeseries = getFirstTimeseries(results.time_comparison?.series ?? {});
  let comparisonDates = Object.keys(comparisonTimeseries);

  if ((dates.length === 0 || comparisonDates.length === 0) && results.time_comparison?.date_range) {
    // if either main or time comparison series are empty, we have to infer dates based on the offset
    const offsetMs = getTimeComparisonOffsetMs({
      dateRange: {
        fromDate: results.date_range.from_date,
        toDate: results.date_range.to_date,
      },
      timeComparisonDateRange: {
        fromDate: results.time_comparison.date_range.from_date,
        toDate: results.time_comparison.date_range.to_date,
      },
    });
    const dateFormat = moment.parseZone(results.date_range.from_date, moment.ISO_8601).creationData().format.toString();

    if (dates.length === 0 && comparisonDates.length > 0) {
      dates = comparisonDates.map((date) => {
        return moment.parseZone(date, moment.ISO_8601).add(offsetMs, `ms`).format(dateFormat);
      });
    }

    if (dates.length > 0 && comparisonDates.length === 0) {
      comparisonDates = dates.map((date) => {
        return moment.parseZone(date, moment.ISO_8601).subtract(offsetMs, `ms`).format(dateFormat);
      });
    }
  }

  return {main: dates, timeComparison: comparisonDates};
}

/**
 * Return an array of zero-value flat segments that are missing from @reference (missing-check is based on segment key).
 * @param {FlatSegment[]} reference - array of flat segments to check against
 * @param {FlatSegment[]} subset - array of flat segments that may be missing segments compared to @reference
 * @param {FlatSegment[]} timeseriesDates - array of ISO 8601 date strings to use as object keys for the output segment values
 * @returns {FlatSegment[]} - array of flat segments with zero-value segments; the segment value will be an empty object if @subset is an empty array
 */
export function getMissingZeroSegments(
  reference: FlatSegment[],
  subset: FlatSegment[],
  timeseriesDates: string[],
): FlatSegment[] {
  const missingSegments = differenceBy(reference, subset, `key`);
  const zeroTimeseries = timeseriesDates.reduce<Record<string, number>>((timeseries, date): Record<string, number> => {
    timeseries[date] = 0;
    return timeseries;
  }, {});
  return missingSegments.map((segment) => {
    return {
      ...segment,
      value: zeroTimeseries,
    };
  });
}

export function nestedObjectDepthInsightsFormat(obj) {
  return nestedObjectDepth(obj) - 1;
}

export function getNestedHeaderTypes(obj: InsightsSeriesTree): HeaderType[] {
  function _getNestedHeaderTypes(obj, types, maxDepth, currDepth) {
    if (currDepth === 0) {
      return;
    }

    for (const [key, value] of Object.entries(obj)) {
      const index = maxDepth - currDepth;
      if (!Object.prototype.hasOwnProperty.call(types, index)) {
        const headerType = getHeaderType(key);
        if (headerType) {
          types[index] = headerType;
        }
      }
      _getNestedHeaderTypes(value, types, maxDepth, currDepth - 1);
      if (Object.keys(types).length === maxDepth) {
        // Early termination of depth-first traversal if types at all depths have been determined
        return;
      }
    }
  }

  const types = [];
  const depth = nestedObjectDepthInsightsFormat(obj);
  _getNestedHeaderTypes(obj, types, depth, depth);
  return types;
}

/**
 * Check if insights series data is a leaf. `all` is for event queries, `value` is for profile queries.
 * query-api returns `null` for constant formulas not associated with any show clauses.
 */
function getValueIfLeaf(series: InsightsSeriesTree): number | null {
  for (const leafKey of [`all`, `value`, `null`]) {
    if (leafKey in series && typeof series[leafKey] === `number`) {
      return round(series[leafKey] as number, NUMERIC_DATA_PRECISION);
    }
  }

  return null;
}

/**
 * Extract a single number from single-number flat segments. `all` is for event queries, `value` is for profile queries.
 * query-api returns `null` for constant formulas not associated with any show clauses.
 */
export function getSingleNumberFromSegment(segment: FlatSegment): number {
  return segment.value[`all`] ?? segment.value[`value`] ?? segment.value[`null`];
}

/**
 * Given an object with numeric leaf values, transform each leaf to zero
 */
function deepMapValuesToZero(obj: Obj | number) {
  return isObject(obj) ? mapValues(obj, (innerValue) => deepMapValuesToZero(innerValue)) : 0;
}

/**
 * Convert an InsightsSeriesTree such that each parent has an nested children object keyed by label
 * and a value which is the sum of all child values.
 */
export function createUnorderedSumTree(
  series: InsightsSeriesTree,
  timeComparisonSeries: InsightsSeriesTree,
  headerTypes: string[],
  currPath: string[] = [],
): UnorderedSumTree {
  const headerType = headerTypes[0];

  const keys = Array.from(new Set([...Object.keys(series), ...Object.keys(timeComparisonSeries ?? {})]));

  return keys.reduce((acc, key) => {
    const path = [...currPath, key];

    // if key is not present in the main series but is in the time comparison series,
    // pad the main series with zero values from the time comparison series.
    if (!Object.prototype.hasOwnProperty.call(series, key)) {
      series[key] = deepMapValuesToZero(timeComparisonSeries[key]);
    }

    const leafValue = getValueIfLeaf(series[key] as InsightsSeriesTree);
    if (leafValue !== null) {
      acc[key] = {
        label: key,
        value: leafValue,
        timeComparisonValue: getValueIfLeaf((timeComparisonSeries?.[key] ?? {}) as InsightsSeriesTree) ?? 0,
        headerType,
        path,
      };
    } else {
      const children = createUnorderedSumTree(
        series[key] as InsightsSeriesTree,
        timeComparisonSeries?.[key] as InsightsSeriesTree,
        headerTypes.slice(1),
        path,
      );

      acc[key] = {
        label: key,
        value: sumBy(Object.values(children), `value`),
        timeComparisonValue: sumBy(Object.values(children), `timeComparisonValue`),
        children,
        headerType,
        path,
      };
    }

    return acc;
  }, {});
}

/**
 * Flatten a sum tree such that each node has at most one child.
 * Used to calculate sort by value.
 */
function getFlattenedOrderedSumTree(tree: UnorderedSumTree): OrderedSumTree[] {
  const nodes = Object.values(tree);

  if (nodes[0].children) {
    return nodes.flatMap((node) => {
      return getFlattenedOrderedSumTree(node.children).map((child) => ({
        ...node,
        value: child.value,
        timeComparisonValue: child.timeComparisonValue,
        children: [child],
      }));
    });
  } else {
    return nodes.map((node) => omit(node, `children`));
  }
}

function sortSumTreeByColumn(tree: UnorderedSumTree, colSortAttrs: FlatSortingOptions[]): OrderedSumTree[] {
  const currColSortAttrs = colSortAttrs[0];
  const nodes = Object.values(tree);

  return nodes
    .map((node) => {
      if (node.children) {
        return {...node, children: sortSumTreeByColumn(node.children, colSortAttrs.slice(1))};
      }

      return omit(node, `children`);
    })
    .sort(
      typedComparator({
        sortType: currColSortAttrs.sortBy === SortBy.Value ? HeaderType.NUMBER : nodes[0].headerType,
        order: currColSortAttrs.sortOrder,
        transform: (item) => item[currColSortAttrs.sortBy].toString(),
      }),
    );
}

function sortSumTreeByValue(tree: UnorderedSumTree, order: SortOrder): OrderedSumTree[] {
  return getFlattenedOrderedSumTree(tree).sort(
    typedComparator({
      sortType: HeaderType.NUMBER,
      order,
      transform: (item) => item.value.toString(),
    }),
  );
}

export function sortSumTree(tree: UnorderedSumTree, sortConfig: SeriesSortingOptions): OrderedSumTree[] {
  if (isEmpty(tree)) {
    return [];
  }

  switch (sortConfig.sortBy) {
    case SortBy.Value:
      return sortSumTreeByValue(tree, sortConfig.sortOrder);
    case SortBy.Column:
      return sortSumTreeByColumn(tree, sortConfig.colSortAttrs);
    default:
      throw new Error(`Unsupported sortBy type: ${sortConfig.sortBy}`);
  }
}

/**
 * Given a sum tree, merge all nodes at each depth by label and add their corresponding values together
 * @returns {AggregatedSeries[]} - an array indexed by depth where the values are
 * objects with the node label as key, and an object with total and list of paths as value
 */
export function getAggregatedSeriesList(tree: UnorderedSumTree): AggregatedSeries[] {
  function helper(nodes: UnorderedSumTreeNode[], aggregatedSeries) {
    const aggregated = nodes.reduce((acc, node) => {
      if (getOwnAttr(acc, node.label)) {
        acc[node.label].total += node.value;
        acc[node.label].paths.push(node.path);
      } else {
        acc[node.label] = {
          total: node.value,
          paths: [node.path],
        };
      }

      return acc;
    }, {});

    const combined = [...aggregatedSeries, aggregated];

    if (nodes[0]?.children) {
      const children = nodes.flatMap((node) => Object.values(node.children));
      return helper(children, combined);
    } else {
      return combined;
    }
  }

  return helper(Object.values(tree), []);
}

/**
 * Sort an aggregated series by sortOption.
 * Attempts to get the header type of the series for more accurate sorting.
 */
export function getSortedAggregatedSeriesKeys(
  aggregatedSeries: AggregatedSeries,
  sortOption: FlatSortingOptions,
): string[] {
  const seriesKeys = Object.keys(aggregatedSeries);

  let sortType;
  if (sortOption.sortBy === SortBy.Value) {
    sortType = HeaderType.NUMBER;
  } else {
    for (const label of seriesKeys) {
      if (label !== `undefined`) {
        sortType = getHeaderType(label);
        break;
      }
    }

    sortType = sortType || HeaderType.STRING;
  }

  return seriesKeys.sort(
    typedComparator({
      sortType,
      order: sortOption.sortOrder,
      transform: (item) => {
        return sortOption.sortBy === SortBy.Value ? aggregatedSeries[item].total.toString() : item;
      },
    }),
  );
}

/**
 * Given an aggregated series, returns a corresponding object where the value is the index at which it would apppear
 * when sorting by the value of that node.
 */
export function getSortedKeyMap(aggregatedSeries: AggregatedSeries): {[label: string]: number} {
  const sortedKeys = sorted(Object.keys(aggregatedSeries), {
    order: `desc`,
    transform: (key) => Math.abs(aggregatedSeries[key].total),
  });

  return sortedKeys.reduce((keyMap, key, idx) => {
    keyMap[key] = idx;
    return keyMap;
  }, {});
}

export function shouldShowSeriesTotal(sections: InsightsBookmarkSections): boolean {
  if (sections.cohorts?.length) {
    return false;
  }

  if (sections.show.some((clause) => clause.math !== PRIMATIVE_MATH_TYPES.TOTAL)) {
    return false;
  }

  if (sections.group?.some((clause) => clause.propertyType === PropertyType.List)) {
    return false;
  }
  return true;
}

export const INSIGHTS_RESOURCE_TYPE_TO_COMMON_RESOURCE_TYPE = {
  [InsightsResourceType.All]: ResourceType.Event,
  [InsightsResourceType.Events]: ResourceType.Event,
  [InsightsResourceType.Cohorts]: ResourceType.Cohort,
  [InsightsResourceType.People]: ResourceType.User,
};

/**
 * Derive series header display names using bookmark params as query-api does not always return lexicon rename-able
 * headers in results (i.e. for nested object props, data groups).
 */
export function getSeriesDisplayHeaders(
  sections: InsightsBookmarkSections,
  resultHeaders: string[],
  dataGroups: Record<string, DataGroup>,
): string[] {
  // All show clauses for series data share the same header / resource type
  const {resourceType, dataGroupId} = sections.show[0];

  let metricHeader;

  // Use data group display name instead of $people header
  if (resourceType === InsightsResourceType.People) {
    metricHeader = dataGroupId && dataGroupId in dataGroups ? dataGroups[dataGroupId].displayName : `Users`;
  } else {
    metricHeader = renameProperty(resultHeaders[0], INSIGHTS_RESOURCE_TYPE_TO_COMMON_RESOURCE_TYPE[resourceType], {
      dataGroupId,
    });
  }

  // Cohort clauses are always grouped into the same header and always come first after the `show` header
  const hasCohorts = sections.cohorts?.length;

  // Distribution header will always be last, only need one show clause with distribution
  const hasDistributionAggregation = sections.show.some((c) => c.math === PRIMATIVE_MATH_TYPES.HISTOGRAM);

  const groupHeaders = sections.group.map((clause) => {
    const {dataGroupId, resourceType, value, propertyObjectKey, customProperty} = clause;
    const commonResourceType = INSIGHTS_RESOURCE_TYPE_TO_COMMON_RESOURCE_TYPE[resourceType];

    let suffix = ``;

    // nested object property support
    if (propertyObjectKey) {
      suffix = Array.isArray(propertyObjectKey) ? propertyObjectKey.join(`.`) : propertyObjectKey;
      suffix = `.${suffix}`;
    }

    return renameProperty(value, commonResourceType, {dataGroupId, customProperty}) + suffix;
  });

  const derivedHeaders = [
    metricHeader,
    ...(hasCohorts ? [`Cohort`] : []),
    ...groupHeaders,
    ...(hasDistributionAggregation ? [`Distribution`] : []),
  ];

  if (derivedHeaders.length !== resultHeaders.length) {
    reporter.error(`Unable to accurately derive insights series headers`, {derivedHeaders, resultHeaders});
    return resultHeaders;
  }

  return derivedHeaders;
}

export function showHighCardinality() {
  return featuresStore?.value?.hasProjectFeatureFlag(ProjectFeatureFlag.HighCardinality);
}
